import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MeetingBooksLinkApiService } from '../../services/meeting-books-link-api.service';
import {
  BookingReminderMethod,
  BookingReminderTarget,
  MeetingReminderConfigRequest,
  MeetingReminderConfigResponse,
  MeetingEmailTemplateResponse,
} from '../../models/meeting.model';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { MeetingEmailTemplateApiService } from '../../services/meeting-email-template-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';

@Component({
  selector: 'app-booking-reminder-configs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './booking-reminder-configs.component.html',
})
export class BookingReminderConfigsComponent implements OnChanges {
  @Input() bookingLinkId!: number;

  readonly methods: BookingReminderMethod[] = ['email', 'sms', 'whatsapp'];
  readonly targets: BookingReminderTarget[] = ['invitee', 'host', 'both'];

  /** Etiquetas en español (se conserva el value técnico) — INC 57. */
  readonly methodLabels: Record<BookingReminderMethod, string> = {
    email: 'Correo electrónico',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
  };
  readonly targetLabels: Record<BookingReminderTarget, string> = {
    invitee: 'Invitado',
    host: 'Anfitrión',
    both: 'Ambos',
  };

  items: MeetingReminderConfigResponse[] = [];
  loading = false;
  saving = false;

  /** Plantillas de correo de reuniones para el selector de "Plantilla" (INC 57). */
  templates: MeetingEmailTemplateResponse[] = [];

  newMinutes: number | null = null;
  newMethod: BookingReminderMethod = 'email';
  newTarget: BookingReminderTarget = 'invitee';
  newEmailTemplateId: number | null = null;

  constructor(
    private api: MeetingBooksLinkApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private templateApi: MeetingEmailTemplateApiService,
    private auth: AuthService
  ) {}

  ngOnChanges(c: SimpleChanges): void {
    if (c['bookingLinkId'] && this.bookingLinkId) {
      this.load();
      this.loadTemplates();
    }
  }

  private loadTemplates(): void {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    this.templateApi
      .list(businessId, 0, 200)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (res) this.templates = res.content ?? [];
      });
  }

  /** Nombre legible de una plantilla por id, para la lista de recordatorios (INC 57). */
  templateName(id?: number): string {
    if (!id) return '';
    const t = this.templates.find((x) => x.id === id);
    return t ? t.name : `Plantilla #${id}`;
  }

  load(): void {
    this.loading = true;
    this.api.listReminderConfigs(this.bookingLinkId).subscribe({
      next: (data) => {
        this.items = [...data].sort((a, b) => a.minutesBefore - b.minutesBefore);
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  add(): void {
    if (!this.newMinutes || this.newMinutes < 1) return;
    this.saving = true;
    const body: MeetingReminderConfigRequest = {
      minutesBefore: +this.newMinutes,
      method: this.newMethod,
      target: this.newTarget,
      idEmailTemplate: this.newEmailTemplateId ?? undefined,
      isActive: true,
    };
    this.api.createReminderConfig(this.bookingLinkId, body).subscribe({
      next: () => {
        this.saving = false;
        this.newMinutes = null;
        this.newEmailTemplateId = null;
        this.load();
        this.toast.created('Recordatorio');
      },
      error: (err) => {
        this.saving = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear' });
      },
    });
  }

  toggle(item: MeetingReminderConfigResponse): void {
    this.api
      .updateReminderConfig(this.bookingLinkId, item.id, {
        minutesBefore: item.minutesBefore,
        method: item.method,
        target: item.target,
        idEmailTemplate: item.idEmailTemplate,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.load() });
  }

  async remove(item: MeetingReminderConfigResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar recordatorio',
      message: '¿Eliminar este recordatorio?'
    });
    if (!ok) return;
    this.api.deleteReminderConfig(this.bookingLinkId, item.id).subscribe({
      next: () => { this.load(); this.toast.deleted('Recordatorio'); },
    });
  }
}
